/**
 * AdminContentEngine — /admin/content-engine
 *
 * The CMS control panel for the AI Content Engine. Admin can:
 *   • Pick a portal → see its current schedule
 *   • Create a new article by giving topic + geography (calls GPT-5.2)
 *   • Browse existing articles per portal
 *   • Trigger `review-now` / `republish` / `translate` / `delete`
 *   • Configure per-portal schedule (enabled, run hour UTC, batch size,
 *     review cadence days) — the backend scheduler picks it up
 *   • Manually kick off a freshness sweep across all portals or one
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Plus, RefreshCw, Trash2, Globe2, Clock, PlayCircle,
  BadgeCheck, Languages, ExternalLink, Eye,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl bg-white ring-1 ring-slate-200 p-5 ${className}`}>{children}</div>;
}

function StatusChip({ status }) {
  const map = {
    published:  "bg-emerald-50 text-emerald-700 ring-emerald-200",
    draft:      "bg-slate-100 text-slate-600 ring-slate-200",
    "insufficient": "bg-rose-50 text-rose-700 ring-rose-200",
  };
  const cls = map[status] || map.draft;
  return <span className={`inline-flex items-center rounded-full ring-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cls}`}>{status}</span>;
}

export default function AdminContentEngine() {
  const { user, loading: authLoading } = useAuth();
  const [portals, setPortals] = useState([]);
  const [portal, setPortal] = useState("socialpulse");
  const [articles, setArticles] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  // Create form
  const [topic, setTopic] = useState("");
  const [geography, setGeography] = useState("");
  const [extra, setExtra] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [portRes, listRes, schedRes] = await Promise.all([
        http.get("/content-engine/portals"),
        http.get("/content-engine/articles", { params: { portal, limit: 50 } }),
        http.get(`/content-engine/admin/portals/${portal}/schedule`),
      ]);
      setPortals(portRes.data.items || []);
      setArticles(listRes.data.items || []);
      setSchedule(schedRes.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [portal]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const create = async () => {
    if (!topic.trim()) return toast.error("Topic is required.");
    setCreating(true);
    try {
      const r = await http.post("/content-engine/articles", {
        portal_slug: portal,
        topic: topic.trim(),
        geography: geography.trim() || null,
        extra_context: extra.trim() || null,
      });
      toast.success(`Created: ${r.data.master?.title || r.data.slug}`);
      setTopic(""); setGeography(""); setExtra("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.insufficient) toast.error(`INSUFFICIENT: ${d.reason || "sources unavailable"}`);
      else toast.error(d || e.message);
    } finally { setCreating(false); }
  };

  const doAction = async (label, url, method = "post") => {
    setBusyKey(label);
    try {
      await http[method](url);
      toast.success(`${label} ✔`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? JSON.stringify(d) : (d || e.message));
    } finally { setBusyKey(null); }
  };

  const saveSchedule = async () => {
    setBusyKey("sched");
    try {
      await http.put(`/content-engine/admin/portals/${portal}/schedule`, schedule);
      toast.success("Schedule saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusyKey(null); }
  };

  const runNow = async () => {
    setBusyKey("run-now");
    try {
      const r = await http.post("/content-engine/admin/scheduler/run-now", null, { params: { portal } });
      toast.success(`Processed ${r.data.processed} article(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusyKey(null); }
  };

  if (authLoading) return (
    <div className="min-h-screen bg-white">
      <SocialPulseHeader />
      <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      <SocialPulseFooter />
    </div>
  );
  if (!user) return (
    <div className="min-h-screen bg-white">
      <SocialPulseHeader />
      <div className="min-h-[60vh] grid place-items-center text-center px-6">
        <div>
          <p className="text-slate-700 font-serif text-2xl">Sign in required</p>
          <p className="text-slate-500 mt-2 text-sm">The AI Content Engine CMS is admin-only.</p>
          <Link to="/join" className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2 text-sm mt-5" data-testid="admin-ce-signin">
            Sign in →
          </Link>
        </div>
      </div>
      <SocialPulseFooter />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-content-engine-page">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[.22em] text-violet-700 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> AI Content Engine
            </div>
            <h1 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl mt-1">Content CMS</h1>
            <p className="text-sm text-slate-500 mt-0.5">Research · Verify · Create · Tag · Translate · Publish · Monitor · Review</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/admin/content-engine/report" className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 hover:bg-violet-100 text-violet-700 ring-1 ring-violet-200 text-xs font-semibold px-3 py-1.5" data-testid="admin-ce-goto-report">
              <BadgeCheck className="w-3 h-3" /> Progress report
            </Link>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Portal</label>
            <select
              value={portal}
              onChange={(e) => setPortal(e.target.value)}
              data-testid="admin-ce-portal-select"
              className="rounded-full bg-white ring-1 ring-slate-300 focus:ring-violet-500 focus:outline-none pl-3 pr-6 py-1.5 text-sm font-semibold text-slate-700"
            >
              {portals.map((p) => (
                <option key={p.slug} value={p.slug}>{p.portal_name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* ─── Left column: create form + list ─── */}
          <div className="space-y-6">
            {/* Create form */}
            <Card>
              <h2 className="font-serif font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4 text-violet-600" /> Create a new article
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block sm:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Topic</span>
                  <input
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder='e.g. "Kadanad village, Kerala" or "Understanding Social Capital"'
                    data-testid="admin-ce-topic-input"
                    className="mt-1 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 focus:outline-none px-3 py-2 text-sm text-slate-800"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Geography (optional)</span>
                  <input
                    value={geography}
                    onChange={(e) => setGeography(e.target.value)}
                    placeholder="Kottayam district, Kerala, India"
                    data-testid="admin-ce-geo-input"
                    className="mt-1 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 focus:outline-none px-3 py-2 text-sm text-slate-800"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Extra context (optional)</span>
                  <input
                    value={extra}
                    onChange={(e) => setExtra(e.target.value)}
                    placeholder="High-range village. Part of Meenachil taluk."
                    data-testid="admin-ce-extra-input"
                    className="mt-1 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 focus:outline-none px-3 py-2 text-sm text-slate-800"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={create}
                disabled={creating || !topic.trim()}
                data-testid="admin-ce-create-btn"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2 text-sm disabled:opacity-60"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Research & generate
              </button>
              <p className="mt-2 text-[11px] text-slate-500">Uses GPT-5.2 for research + Unsplash for imagery. Translations happen on demand.</p>
            </Card>

            {/* Article list */}
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif font-bold text-lg text-slate-900">Articles ({articles.length})</h2>
                <button
                  type="button"
                  onClick={load}
                  className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-slate-300 hover:ring-slate-400 text-xs font-semibold text-slate-600 px-3 py-1"
                  data-testid="admin-ce-reload"
                >
                  <RefreshCw className="w-3 h-3" /> Reload
                </button>
              </div>

              {articles.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No articles yet. Create your first one above.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {articles.map((a) => (
                    <li key={a.id} className="py-3 flex flex-wrap items-start gap-3" data-testid={`admin-ce-article-${a.slug}`}>
                      {a.image?.url_thumb && (
                        <img src={a.image.url_thumb} alt="" className="w-14 h-14 rounded-lg object-cover" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/pulse-article/${a.slug}?portal=${a.portal_slug}`}
                            className="font-semibold text-slate-900 text-sm hover:text-violet-700 truncate"
                          >
                            {a.master?.title || a.slug}
                          </Link>
                          <StatusChip status={a.editorial_status} />
                          <span className="text-[10px] text-slate-500">v{a.content_version}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {a.portal_name} · {(a.tags?.content_type || "reference")} · Next review {new Date(a.next_review_date).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/pulse-article/${a.slug}?portal=${a.portal_slug}`}
                          data-testid={`admin-ce-view-${a.slug}`}
                          className="inline-flex items-center gap-1 rounded-full ring-1 ring-slate-300 hover:ring-slate-400 text-xs font-semibold text-slate-600 px-2 py-1"
                          title="View article"
                        >
                          <Eye className="w-3 h-3" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => doAction("review-now", `/content-engine/articles/${a.id}/review-now`)}
                          disabled={busyKey === "review-now"}
                          data-testid={`admin-ce-review-${a.slug}`}
                          className="inline-flex items-center gap-1 rounded-full ring-1 ring-slate-300 hover:ring-emerald-400 text-xs font-semibold text-slate-600 hover:text-emerald-700 px-2 py-1"
                          title="Review now"
                        >
                          <BadgeCheck className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => doAction("translate-ml", `/content-engine/articles/${a.id}/translate?lang=ml`)}
                          disabled={busyKey === "translate-ml"}
                          data-testid={`admin-ce-translate-ml-${a.slug}`}
                          className="inline-flex items-center gap-1 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-xs font-semibold text-slate-600 hover:text-violet-700 px-2 py-1"
                          title="Translate to Malayalam"
                        >
                          <Languages className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => { if (window.confirm(`Delete "${a.master?.title || a.slug}"?`)) doAction("delete", `/content-engine/articles/${a.id}`, "delete"); }}
                          data-testid={`admin-ce-delete-${a.slug}`}
                          className="inline-flex items-center gap-1 rounded-full ring-1 ring-slate-300 hover:ring-rose-400 text-xs font-semibold text-slate-600 hover:text-rose-700 px-2 py-1"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {/* ─── Right column: schedule + actions ─── */}
          <div className="space-y-6">
            <Card>
              <h2 className="font-serif font-bold text-lg text-slate-900 flex items-center gap-2">
                <Clock className="w-4 h-4 text-violet-600" /> Freshness schedule
              </h2>
              {schedule ? (
                <div className="mt-3 space-y-3">
                  <label className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">Enabled</span>
                    <input
                      type="checkbox"
                      checked={!!schedule.enabled}
                      onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })}
                      data-testid="admin-ce-sched-enabled"
                      className="w-4 h-4"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-slate-700">Run hour (UTC)</span>
                    <input
                      type="number" min={0} max={23}
                      value={schedule.run_hour_utc ?? 3}
                      onChange={(e) => setSchedule({ ...schedule, run_hour_utc: Number(e.target.value) })}
                      data-testid="admin-ce-sched-hour"
                      className="mt-1 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 px-3 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-slate-700">Batch size</span>
                    <input
                      type="number" min={1} max={200}
                      value={schedule.batch_size ?? 25}
                      onChange={(e) => setSchedule({ ...schedule, batch_size: Number(e.target.value) })}
                      data-testid="admin-ce-sched-batch"
                      className="mt-1 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 px-3 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-slate-700">Default review cadence (days)</span>
                    <input
                      type="number" min={1} max={365}
                      value={schedule.review_cadence_days ?? 30}
                      onChange={(e) => setSchedule({ ...schedule, review_cadence_days: Number(e.target.value) })}
                      data-testid="admin-ce-sched-cadence"
                      className="mt-1 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 px-3 py-1.5 text-sm"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={saveSchedule}
                    disabled={busyKey === "sched"}
                    data-testid="admin-ce-sched-save"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 text-sm disabled:opacity-60"
                  >
                    {busyKey === "sched" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save schedule"}
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-500 mt-2">Loading…</p>
              )}
            </Card>

            <Card>
              <h2 className="font-serif font-bold text-lg text-slate-900 flex items-center gap-2">
                <PlayCircle className="w-4 h-4 text-emerald-600" /> Manual sweep
              </h2>
              <p className="text-xs text-slate-500 mt-1">Kick the freshness scheduler now for <b>{portal}</b>. Only reviews articles whose <code>next_review_date</code> has passed.</p>
              <button
                type="button"
                onClick={runNow}
                disabled={busyKey === "run-now"}
                data-testid="admin-ce-run-now"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2 text-sm disabled:opacity-60"
              >
                {busyKey === "run-now" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run freshness sweep"}
              </button>
            </Card>

            <Card>
              <h2 className="font-serif font-bold text-lg text-slate-900 flex items-center gap-2">
                <Globe2 className="w-4 h-4 text-sky-600" /> Portal registry
              </h2>
              <ul className="mt-2 space-y-1.5">
                {portals.map((p) => (
                  <li key={p.slug} className="text-xs">
                    <span className={`font-semibold ${p.slug === portal ? "text-violet-700" : "text-slate-700"}`}>{p.portal_name}</span>
                    <span className="text-slate-400"> · {p.slug}</span>
                  </li>
                ))}
              </ul>
              <a href="/app/docs/CONTENT_ENGINE_INSTALL.md" className="mt-3 inline-flex items-center gap-1 text-xs text-violet-700 hover:underline" data-testid="admin-ce-install-doc">
                Install on a new portal <ExternalLink className="w-3 h-3" />
              </a>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
